import { Button, CloseButton, Dialog, Field, HStack, Input, Portal, Spinner, Text, VStack } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import ClientService from "@/services/ClientService";
import { showToast } from "@/components/ui/toaster";
import { companyLabel } from "../companyLabel";

const empty = { firstName: "", lastName: "", email: "" };

/**
 * Add a user to a company (mode="add" — sends an invitation) or edit an existing member's
 * name / e-mail (mode="edit"). Company + billing data is never edited here.
 */
const MemberFormDialog = ({ open, mode, company, member, onClose, onSaved }) => {
    const [form, setForm] = useState(empty);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm(mode === "edit" && member
            ? { firstName: member.firstName ?? "", lastName: member.lastName ?? "", email: member.email ?? "" }
            : empty);
    }, [open, mode, member]);

    const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

    const handleSave = async () => {
        if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
            showToast("Error", "All fields are required", "error");
            return;
        }
        setIsSaving(true);
        try {
            if (mode === "edit") {
                await ClientService.updateMember(member.id, form);
                showToast("User updated", null, "success");
            } else {
                await ClientService.addMember(company.id, form);
                showToast("User invited", "Activation email sent", "success");
            }
            onSaved();
            onClose();
        } catch (err) {
            showToast("Error", err?.response?.data ?? "Failed to save user", "error");
        } finally {
            setIsSaving(false);
        }
    };

    const title = mode === "edit"
        ? `Edit ${member?.firstName} ${member?.lastName}`
        : `Add user to ${companyLabel(company)}`;

    return (
        <Dialog.Root lazyMount open={open} onOpenChange={(e) => !e.open && !isSaving && onClose()} placement="center">
            <Portal>
                <Dialog.Backdrop />
                <Dialog.Positioner>
                    <Dialog.Content bg="bg.card" borderWidth={1} borderColor="border.secondary" borderRadius={12} p={2} minW={96}>
                        <Dialog.Header>
                            <Dialog.Title color="white">{title}</Dialog.Title>
                            <Dialog.CloseTrigger asChild>
                                <CloseButton color="white" size="sm" disabled={isSaving} />
                            </Dialog.CloseTrigger>
                        </Dialog.Header>
                        <Dialog.Body>
                            <VStack gap={4} align="flex-start" w="full">
                                <HStack w="full" gap={3} align="flex-start" flexWrap="wrap">
                                    <Field.Root required flex="1" minW="160px">
                                        <Field.Label color="white" fontSize={14}>First name <Field.RequiredIndicator /></Field.Label>
                                        <Input variant="primary" fontSize={16} py={5} value={form.firstName} onChange={set("firstName")} />
                                    </Field.Root>
                                    <Field.Root required flex="1" minW="160px">
                                        <Field.Label color="white" fontSize={14}>Last name <Field.RequiredIndicator /></Field.Label>
                                        <Input variant="primary" fontSize={16} py={5} value={form.lastName} onChange={set("lastName")} />
                                    </Field.Root>
                                </HStack>
                                <Field.Root required w="full">
                                    <Field.Label color="white" fontSize={14}>Email <Field.RequiredIndicator /></Field.Label>
                                    <Input variant="primary" fontSize={16} py={5} type="email" value={form.email} onChange={set("email")} />
                                </Field.Root>
                                {mode === "add" && (
                                    <Text color="text.grey" fontSize="xs">
                                        The user receives an activation email and stays inactive until they set a password.
                                    </Text>
                                )}
                            </VStack>
                        </Dialog.Body>
                        <Dialog.Footer>
                            <Button variant="outlined" onClick={onClose} disabled={isSaving}>Cancel</Button>
                            <Button variant="primary" onClick={handleSave} disabled={isSaving}>
                                {isSaving ? <Spinner size="sm" color="white" /> : mode === "edit" ? "Save" : "Send invitation"}
                            </Button>
                        </Dialog.Footer>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    );
};

export default MemberFormDialog;
